import React, { useMemo } from 'react';
import { maxValidator, minValidator, required, requiredNonZero } from '~/app/helpers/validation';
import Loader from '~/app/common/Loader';
import useForm, { UseFormOptionFields } from '~/app/modules/form/useForm';
import { ServiceCompletePayload, ServiceDetails } from '~/types';
import classes from './style.module.scss';
import useDidMountEffect from '~/app/hooks/useDidMountEffect';
import Details from './Details';
import Footer from './Footer';
import { useStoreState } from '~/store';
import { ValidatorsEnum } from '~/app/modules/form';
import { getDynamicScheduleAt } from '~/services/helpers';

export type FormProps = {
    onClose(): void
    serviceDetails: ServiceDetails
    onSave?: (id: ServiceDetails['id']) => void
}

export type FormComponent = React.FC<FormProps>

const Form: FormComponent = ({ onClose, serviceDetails, onSave }) => {
    const isHPCodeRequired = useStoreState(state => state.issue.isHPCodeRequired);
    const hasNextServiceSection = useStoreState(state => state.service.hasNextServiceSection);

    const getDefaultNextDynamicServiceOccurrence = useMemo(() => {
        return (serviceDetails?.operatingTime ?? 0) + (serviceDetails?.primaryMeterUnit ?? 0)
    }, [serviceDetails?.operatingTime, serviceDetails?.primaryMeterUnit]);

    const getDefaultNextDynamicScheduleAt = useMemo(() => {
        let currentDate = new Date();
        if (serviceDetails?.secondaryMeterUnit) {
            const defaultNextDynamicScheduleAt = getDynamicScheduleAt(currentDate, serviceDetails.secondaryMeterUnit, serviceDetails?.timeUnit);
            return defaultNextDynamicScheduleAt.toString();
        }
        return undefined;
    }, [serviceDetails.secondaryMeterUnit, serviceDetails.timeUnit])

    const formFields = useMemo(() => {
        const fields: UseFormOptionFields = [
            ['comment', {}],
            ['ordinaryHours', { validators: [['required', required]] }],
            ['overTime50', {}],
            ['overTime100', {}],
            ['workType', { validators: [['required', requiredNonZero]] }]
        ];

        if (isHPCodeRequired) {
            fields.push(['hpCode', { validators: [['required', requiredNonZero]] } ]);
        }

        if (hasNextServiceSection) {
            if (serviceDetails?.primaryMeterUnit !== null) {
                fields.push(['nextDynamicServiceOccurrence', { validators: [
                    ['required', required],
                    [ValidatorsEnum.min, minValidator(Number(serviceDetails.operatingTime))]
                ] }]);
                fields.push(['nextAlertServiceOccurrence', { validators: [
                    ['required', required],
                    [ValidatorsEnum.min, minValidator(1)],
                    [ValidatorsEnum.max, maxValidator(Number(serviceDetails.primaryMeterUnit))]
                ] }]);
            }

            if (serviceDetails?.scheduleAt !== null) {
                fields.push(['nextDynamicScheduleAt', { validators: [
                    ['required', required]
                ] }]);
    
                fields.push(['nextAlertScheduleDays', { validators: [
                    ['required', required],
                    [ValidatorsEnum.min, minValidator(1)]
                ] }]);
            }
        }

        return fields;
    }, [serviceDetails.operatingTime, serviceDetails.primaryMeterUnit, serviceDetails.scheduleAt, isHPCodeRequired, hasNextServiceSection]);

    const { values, removeForm } = useForm<ServiceCompletePayload>({
        formName: 'completeService',
        fields: formFields,
        defaultValues: {
            comment: '',
            nextDynamicServiceOccurrence: getDefaultNextDynamicServiceOccurrence,
            nextDynamicScheduleAt: getDefaultNextDynamicScheduleAt,
            nextAlertServiceOccurrence: serviceDetails.alertServiceOccurrence,
            nextAlertScheduleDays: serviceDetails.alertScheduleDays,
            hpCode: 0,
            ordinaryHours: undefined,
            overTime50: undefined,
            overTime100: undefined,
            workType: 0
        }
    });

    useDidMountEffect(() => (() => removeForm()));

    if (!values) {
        return <Loader size="32" />;
    }

    return (
        <>
            <div className={classes['container']}>
                <Details />
            </div>

            <Footer onClose={onClose} serviceDetails={serviceDetails} onSave={onSave} />
        </>
    );
};

export default Form;
